import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';

import { BusinessEvent } from '../../models/businessevent.model';
import { BEWorkflowService } from './beWorkflow.service';
import { BEWorkflowSteps } from './beWorkflow.model';
import { OTA_VehAvail } from '../../models/ota_vehavail.model';
import { Router } from '@angular/router';

@Injectable()
export class BEDataService {
    //core BusinessEvent object
    private _businessEvent: BusinessEvent = new BusinessEvent();

    public isItineraryDisabled: boolean = false;
    public isVehicleSelectionDisabled: boolean = false;
    public isExtrasDisabled: boolean = false;
    public isReviewDisabled: boolean = false;
    public isConfirmationDisabled: boolean = false;

    //public activeStep: string = "";

    public invokeRefreshMenu: Subject<any> = new Subject();
    public invokeSaveFirst: Subject<any> = new Subject();

    //Worklfow management variables

    //
    //constructor
    //
    constructor(private _router: Router, private _beWorkflowService: BEWorkflowService)
    {
        //subscribe to the beWorkflow service's SaveFirst event (triggered if the user tries to navigate using the menu bar, and the current page has pending changes)
        this._beWorkflowService.invokeSaveFirst.subscribe((step: string) => {
            //when triggered, invoke this class's SaveFirst event (to be picked up by any subscribing page components)
            this.invokeSaveFirst.next(step);
        });
    }

    //
    //GET businessEvent management methods
    //
    public getBusinessEvent(): BusinessEvent {
        //return the entire BusinessEvent
        return this._businessEvent;
    }

    public getBusinessEventForStep(currentStep: string, resetBE = false): BusinessEvent {
        //return the BusinessEvent data for the given step
        var businessEvent: BusinessEvent;

        //this._currentForm = currentStep;
        this._beWorkflowService.setCurrentStep(currentStep);
        this.refreshMenu();

        switch (currentStep) {
            case BEWorkflowSteps.beItinerary:
                businessEvent = this.getBusinessEventForItinerary(resetBE);
                break;
            case BEWorkflowSteps.beVehicleSelection:
                businessEvent = this.getBusinessEventForVehicleSelection();
                break;
            case BEWorkflowSteps.beExtras:
                businessEvent = this.getBusinessEventForExtras();
                break;
            case BEWorkflowSteps.beReview:
                businessEvent = this.getBusinessEventForReview();
                break;
            case BEWorkflowSteps.beConfirmation:
                businessEvent = this.getBusinessEventForConfirmation();
                break;
            default:
                businessEvent = this._businessEvent;
                break;
        }

        businessEvent.rentalChanged = this._businessEvent.rentalChanged;

        //return this._businessEvent;
        return businessEvent;
    }

    private getBusinessEventForItinerary(resetBE: boolean): BusinessEvent {
        //reset the base BusinessEvent if required.
        if (resetBE) {
            this.resetBusinessEvent();
        }

        //return the BusinessEvent Itinerary data
        var businessEvent: BusinessEvent = new BusinessEvent();

        businessEvent.vehicleType = this._businessEvent.vehicleType;
        businessEvent.locationOut.locationId = this._businessEvent.locationOut.locationId;
        businessEvent.locationIn.locationId = this._businessEvent.locationIn.locationId;
        businessEvent.dateTimeOut = this._businessEvent.dateTimeOut;
        businessEvent.dateTimeIn = this._businessEvent.dateTimeIn;
        businessEvent.ageRange.ageRangeId = this._businessEvent.ageRange.ageRangeId;
        businessEvent.promotionType.promotionTypeId = this._businessEvent.promotionType.promotionTypeId;
        businessEvent.promoCode = this._businessEvent.promoCode;
        businessEvent.eventMode = this._businessEvent.eventMode;
        businessEvent.vehicleSelected = this._businessEvent.vehicleSelected;

        businessEvent.hasDelivery= this._businessEvent.hasDelivery;
        businessEvent.deliveryAddress = this._businessEvent.deliveryAddress;
        businessEvent.hasCollection = this._businessEvent.hasCollection;
        businessEvent.collectionAddress = this._businessEvent.collectionAddress;
        businessEvent.reference = this._businessEvent.reference;

        return businessEvent;
    }

    private getBusinessEventForVehicleSelection(): BusinessEvent {
        //return the BusinessEvent Vehicle Selection data
        var businessEvent: BusinessEvent = new BusinessEvent();

        businessEvent.vehicleType = this._businessEvent.vehicleType;
        businessEvent.locationOut.locationId = this._businessEvent.locationOut.locationId;
        businessEvent.locationOut.description = this._businessEvent.locationOut.description;
        businessEvent.locationOut.code = this._businessEvent.locationOut.code;
        businessEvent.locationIn.locationId = this._businessEvent.locationIn.locationId;
        businessEvent.locationIn.description = this._businessEvent.locationIn.description;
        businessEvent.locationIn.code = this._businessEvent.locationIn.code;
        businessEvent.dateTimeOut = this._businessEvent.dateTimeOut;
        businessEvent.dateTimeIn = this._businessEvent.dateTimeIn;
        businessEvent.ageRange.ageRangeId = this._businessEvent.ageRange.ageRangeId;
        businessEvent.ageRange.ageApplied = this._businessEvent.ageRange.ageApplied;
        businessEvent.promotionType.promotionTypeId = this._businessEvent.promotionType.promotionTypeId;
        businessEvent.promoCode = this._businessEvent.promoCode;
        businessEvent.vehicleSelected = this._businessEvent.vehicleSelected;

        businessEvent.hasDelivery = this._businessEvent.hasDelivery;
        businessEvent.deliveryAddress = this._businessEvent.deliveryAddress;
        businessEvent.hasCollection = this._businessEvent.hasCollection;
        businessEvent.collectionAddress = this._businessEvent.collectionAddress;
        businessEvent.reference = this._businessEvent.reference;
        businessEvent.payNow = this._businessEvent.payNow;
        
        //clone a copy of the business event's objects by serializing/deserializing them
        if (this._businessEvent.vehicleSelected)
            businessEvent.otaVehAvail = JSON.parse(JSON.stringify(this._businessEvent.otaVehAvail));

        return businessEvent;
    }

    private getBusinessEventForExtras(): BusinessEvent {
        //return the BusinessEvent Extras data
        var businessEvent: BusinessEvent = new BusinessEvent();

        businessEvent.locationOut.locationId = this._businessEvent.locationOut.locationId;
        businessEvent.locationOut.description = this._businessEvent.locationOut.description;
        businessEvent.locationIn.locationId = this._businessEvent.locationIn.locationId;
        businessEvent.locationIn.description = this._businessEvent.locationIn.description;
        businessEvent.dateTimeOut = this._businessEvent.dateTimeOut;
        businessEvent.dateTimeIn = this._businessEvent.dateTimeIn;

        businessEvent.hasDelivery = this._businessEvent.hasDelivery;
        businessEvent.deliveryAddress = this._businessEvent.deliveryAddress;
        businessEvent.hasCollection = this._businessEvent.hasCollection;
        businessEvent.collectionAddress = this._businessEvent.collectionAddress;
        businessEvent.reference = this._businessEvent.reference;
        businessEvent.payNow = this._businessEvent.payNow;

        //clone a copy of the business event's objects by serializing/deserializing them
        businessEvent.otaVehAvail = JSON.parse(JSON.stringify(this._businessEvent.otaVehAvail));

        return businessEvent;
    }

    private getBusinessEventForReview(): BusinessEvent {
        //return the BusinessEvent Review data
        var businessEvent: BusinessEvent = new BusinessEvent();

        businessEvent.refresh = this._businessEvent.refresh;
        businessEvent.vehicleType = this._businessEvent.vehicleType;
        businessEvent.locationOut.locationId = this._businessEvent.locationOut.locationId;
        businessEvent.locationOut.description = this._businessEvent.locationOut.description;
        businessEvent.locationOut.code = this._businessEvent.locationOut.code;
        businessEvent.locationOut.isAirport = this._businessEvent.locationOut.isAirport;
        businessEvent.locationIn.locationId = this._businessEvent.locationIn.locationId;
        businessEvent.locationIn.description = this._businessEvent.locationIn.description;
        businessEvent.locationIn.code = this._businessEvent.locationIn.code;
        businessEvent.locationIn.isAirport = this._businessEvent.locationIn.isAirport;
        businessEvent.dateTimeOut = this._businessEvent.dateTimeOut;
        businessEvent.dateTimeIn = this._businessEvent.dateTimeIn;
        businessEvent.ageRange.ageRangeId = this._businessEvent.ageRange.ageRangeId;
        businessEvent.ageRange.ageApplied = this._businessEvent.ageRange.ageApplied;
        businessEvent.promotionType.promotionTypeId = this._businessEvent.promotionType.promotionTypeId;
        businessEvent.promoCode = this._businessEvent.promoCode;
        businessEvent.flightNumber = this._businessEvent.flightNumber;

        businessEvent.hasDelivery = this._businessEvent.hasDelivery;
        businessEvent.deliveryAddress = this._businessEvent.deliveryAddress;
        businessEvent.hasCollection = this._businessEvent.hasCollection;
        businessEvent.collectionAddress = this._businessEvent.collectionAddress;

        //clone a copy of the business event's objects by serializing/deserializing them
        businessEvent.otaVehAvail = JSON.parse(JSON.stringify(this._businessEvent.otaVehAvail));
        businessEvent.rentingentity = JSON.parse(JSON.stringify(this._businessEvent.rentingentity));
        businessEvent.otaVehRetResRS = JSON.parse(JSON.stringify(this._businessEvent.otaVehRetResRS));
        businessEvent.eventMode = this._businessEvent.eventMode;
        businessEvent.rentalChanged = this._businessEvent.rentalChanged;
        businessEvent.reference = this._businessEvent.reference;
        businessEvent.payNow = this._businessEvent.payNow;

        return businessEvent;
    }

    private getBusinessEventForConfirmation(): BusinessEvent {
        //return the BusinessEvent Confirmation data
        var businessEvent: BusinessEvent = new BusinessEvent();

        businessEvent.locationOut.locationId = this._businessEvent.locationOut.locationId;
        businessEvent.locationOut.description = this._businessEvent.locationOut.description;
        businessEvent.locationOut.code = this._businessEvent.locationOut.code;
        businessEvent.locationOut.isAirport = this._businessEvent.locationOut.isAirport;
        businessEvent.locationIn.locationId = this._businessEvent.locationIn.locationId;
        businessEvent.locationIn.description = this._businessEvent.locationIn.description;
        businessEvent.locationIn.code = this._businessEvent.locationIn.code;
        businessEvent.locationIn.isAirport = this._businessEvent.locationIn.isAirport;
        businessEvent.dateTimeOut = this._businessEvent.dateTimeOut;
        businessEvent.dateTimeIn = this._businessEvent.dateTimeIn;
        businessEvent.ageRange.ageApplied = this._businessEvent.ageRange.ageApplied;
        businessEvent.promotionType.promotionTypeId = this._businessEvent.promotionType.promotionTypeId;
        businessEvent.promoCode = this._businessEvent.promoCode;
        businessEvent.flightNumber = this._businessEvent.flightNumber;

        businessEvent.hasDelivery = this._businessEvent.hasDelivery;
        businessEvent.deliveryAddress = this._businessEvent.deliveryAddress;
        businessEvent.hasCollection = this._businessEvent.hasCollection;
        businessEvent.collectionAddress = this._businessEvent.collectionAddress;

        //clone a copy of the business event's objects by serializing/deserializing them
        businessEvent.otaVehAvail = JSON.parse(JSON.stringify(this._businessEvent.otaVehAvail));
        businessEvent.rentingentity = JSON.parse(JSON.stringify(this._businessEvent.rentingentity));
        businessEvent.otaVehRetResRS = JSON.parse(JSON.stringify(this._businessEvent.otaVehRetResRS));
        businessEvent.eventMode = this._businessEvent.eventMode;
        businessEvent.eventAction = this._businessEvent.eventAction;
        businessEvent.reference = this._businessEvent.reference;
        businessEvent.payNow = this._businessEvent.payNow;

        return businessEvent;
    }

    //
    //SET businessEvent management methods
    //
    public setBusinessEventForStep(currentStep: string, be: BusinessEvent, skipCount: number = 0, validateStep: boolean = true): string {
        let loopCounter: number = 0;

        switch (currentStep) {
            case BEWorkflowSteps.beItinerary:
                this.setBusinessEventForItinerary(be);
                break;
            case BEWorkflowSteps.beVehicleSelection:
                this.setBusinessEventForVehicleSelection(be);
                break;
            case BEWorkflowSteps.beExtras:
                this.setBusinessEventForExtras(be);
                break;
            case BEWorkflowSteps.beReview:
                this.setBusinessEventForReview(be);
                break;
            case BEWorkflowSteps.beConfirmation:
                this.setBusinessEventForConfirmation(be);
                break;
        }

        this._businessEvent.rentalChanged = be.rentalChanged;

        // Validate the step in workflow
        if (validateStep)
            currentStep = this.validateStep(currentStep, skipCount)
        else
            this.invalidateStep(currentStep);

        return currentStep;
    }

    private setBusinessEventForItinerary(be: BusinessEvent) {
        //update the core BusinessEvent object with the Itinerary form data
        this._businessEvent.vehicleType = be.vehicleType;
        this._businessEvent.locationOut.locationId = be.locationOut.locationId;
        this._businessEvent.locationOut.description = be.locationOut.description;
        this._businessEvent.locationOut.code = be.locationOut.code;
        this._businessEvent.locationOut.isAirport = be.locationOut.isAirport;
        this._businessEvent.locationIn.locationId = be.locationIn.locationId;
        this._businessEvent.locationIn.description = be.locationIn.description;
        this._businessEvent.locationIn.code = be.locationIn.code;
        this._businessEvent.locationIn.isAirport = be.locationIn.isAirport;
        this._businessEvent.dateTimeOut = be.dateTimeOut;
        this._businessEvent.dateTimeIn = be.dateTimeIn;
        this._businessEvent.ageRange.ageRangeId = be.ageRange.ageRangeId;
        this._businessEvent.ageRange.ageApplied = be.ageRange.ageApplied;
        this._businessEvent.promotionType.promotionTypeId = be.promotionType.promotionTypeId;
        this._businessEvent.promoCode = be.promoCode;

        this._businessEvent.hasDelivery = be.hasDelivery;
        this._businessEvent.deliveryAddress = be.deliveryAddress;
        this._businessEvent.hasCollection = be.hasCollection;
        this._businessEvent.collectionAddress = be.collectionAddress;

        this._businessEvent.otaVehRetResRS = JSON.parse(JSON.stringify(be.otaVehRetResRS));
        this._businessEvent.eventMode = be.eventMode;
        this._businessEvent.vehicleSelected = be.vehicleSelected;
        this._businessEvent.reference = be.reference;
    }

    private setBusinessEventForVehicleSelection(be: BusinessEvent) {
        //update the core BusinessEvent object with the Vehicle Selection form data
        this._businessEvent.vehicleGroupReserved = be.vehicleGroupReserved;
        this._businessEvent.vehicleSelected = be.vehicleSelected;
        this._businessEvent.payNow = be.payNow;

        if (be.vehicleSelected)
            this._businessEvent.otaVehAvail = JSON.parse(JSON.stringify(be.otaVehAvail));
    }

    private setBusinessEventForExtras(be: BusinessEvent) {
        //update the core BusinessEvent object with the Extras form data
        this._businessEvent.refresh = be.refresh;
        this._businessEvent.otaVehAvail = JSON.parse(JSON.stringify(be.otaVehAvail));

        //update covers selected done as part of the otaVehAvail update
        //update equips requestedCount as part of the otaVehAvail update
    }

    private setBusinessEventForReview(be: BusinessEvent) {
        //update the core BusinessEvent object with the Review form data

        //first name
        //principle name
        //email
        //phone number
        this._businessEvent.flightNumber = be.flightNumber;
        this._businessEvent.rentingentity = JSON.parse(JSON.stringify(be.rentingentity));
        this._businessEvent.otaVehAvail = JSON.parse(JSON.stringify(be.otaVehAvail));
        this._businessEvent.eventAction = be.eventAction;
        this._businessEvent.refresh = false;
    }

    private setBusinessEventForConfirmation(be: BusinessEvent) {
        //unlock the workflow step
        this._beWorkflowService.setCurrentStep(BEWorkflowSteps.beConfirmation, true);

        //reset the core BusinessEvent object
        this.resetBusinessEvent();
    }

    private validateStep(step: string, skipCount: number = 0): string {
        // Validate the step in workflow
        let loopCounter: number = 0;

        do {
            this._beWorkflowService.validateStep(step);
            step = this.nextStep(step);
            loopCounter++;
        } while (loopCounter <= skipCount);

        this.refreshMenu();

        return step;
    }

    public invalidateStep(step: string): string {
        // Invalidate the step in workflow
        this._beWorkflowService.invalidateStep(step);
        this._beWorkflowService.setCurrentStep(step, true);
        this.refreshMenu();

        return step;
    }

    public setCurrentStep(step: string): string {
        this._beWorkflowService.setCurrentStep(step);
        this.refreshMenu();

        return step;
    }

    //
    //workflow management methods
    //
    public changeStep(currentStep: string, isChanged: boolean) {
        //mark the given step as changed or not
        this._beWorkflowService.changeStep(currentStep, isChanged);
        this.refreshMenu();
    }
    
    public firstStep(): string {
        //get the first step
        return this._beWorkflowService.firstStep();
    }

    public nextStep(currentStep: string): string {
        //get the next step
        return this._beWorkflowService.nextStep(currentStep);
    }

    public previousStep(currentStep: string): string {
        //get the next step
        return this._beWorkflowService.previousStep(currentStep);
    }

    private resetBusinessEvent()
    {
        //reset the core BusinessEvent object
        this._businessEvent = new BusinessEvent();

        //reset the workflow
        this._beWorkflowService.resetSteps();    
        this.refreshMenu();
    }

    private refreshMenu()
    {
        var steps = this._beWorkflowService.getStepStatus();

        for (var i = 0; i < steps.length; i++) {
            let item = steps[i];

            switch (item.step) {
                case BEWorkflowSteps.beItinerary:
                    this.isItineraryDisabled = item.disabled;
                    break;
                case BEWorkflowSteps.beVehicleSelection:
                    this.isVehicleSelectionDisabled = item.disabled;
                    break;
                case BEWorkflowSteps.beExtras:
                    this.isExtrasDisabled = item.disabled;
                    break;
                case BEWorkflowSteps.beReview:
                    this.isReviewDisabled = item.disabled;
                    break;
                case BEWorkflowSteps.beConfirmation:
                    this.isConfirmationDisabled = item.disabled;
                    break;
            }
        }

        this.invokeRefreshMenu.next(this._router.url);
    }
}